import type { ClubTheme } from './themes';
import type { SocialAuthor } from './types';
import { useConversations } from './queries';

export interface ConversationSummary {
  id: string;
  kind: 'dm' | 'group';
  title: string;
  participants: SocialAuthor[];
  last_message_at: string | null;
  last_message_snippet: string | null;
  unread_count: number;
  is_muted: boolean;
}

interface ConversationListProps {
  clubId: string;
  theme: ClubTheme;
  activeConvId: string | null;
  onSelect: (convId: string) => void;
  onNewConversation: () => void;
}

export default function ConversationList({
  clubId,
  theme,
  activeConvId,
  onSelect,
  onNewConversation,
}: ConversationListProps) {
  const { data: conversations = [], isLoading: loading } = useConversations(clubId);

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div
        className="flex items-center justify-between border-b px-4 py-3"
        style={{ borderColor: `${theme.colors.muted}20` }}
      >
        <h2 className="text-sm font-bold" style={{ color: theme.colors.text }}>
          Messages
        </h2>
        <button
          type="button"
          onClick={onNewConversation}
          className="rounded-lg px-2.5 py-1 text-xs font-semibold transition-opacity hover:opacity-80"
          style={{ backgroundColor: theme.colors.primary, color: theme.colors.bg }}
        >
          New
        </button>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto">
        {loading && conversations.length === 0 && (
          <p className="px-4 py-6 text-center text-xs" style={{ color: theme.colors.muted }}>
            Loading...
          </p>
        )}

        {!loading && conversations.length === 0 && (
          <div className="px-4 py-8 text-center">
            <p className="text-sm" style={{ color: theme.colors.muted }}>
              No conversations yet
            </p>
            <button
              type="button"
              onClick={onNewConversation}
              className="mt-2 text-xs font-semibold"
              style={{ color: theme.colors.primary }}
            >
              Start one
            </button>
          </div>
        )}

        {conversations.map((conv) => {
          const isActive = conv.id === activeConvId;
          const hasUnread = conv.unread_count > 0;

          return (
            <button
              key={conv.id}
              type="button"
              onClick={() => onSelect(conv.id)}
              className="flex w-full items-start gap-3 px-4 py-3 text-left transition-colors"
              style={{
                backgroundColor: isActive ? `${theme.colors.primary}15` : 'transparent',
              }}
            >
              {/* Avatar stack */}
              <div className="relative mt-0.5 shrink-0">
                {conv.participants[0]?.avatar_url ? (
                  <img
                    src={conv.participants[0].avatar_url}
                    alt=""
                    className="h-9 w-9 rounded-full object-cover"
                  />
                ) : (
                  <div
                    className="flex h-9 w-9 items-center justify-center rounded-full text-sm font-bold"
                    style={{ backgroundColor: `${theme.colors.primary}25`, color: theme.colors.primary }}
                  >
                    {(conv.participants[0]?.full_name?.[0] || '?').toUpperCase()}
                  </div>
                )}
                {hasUnread && (
                  <span
                    className="absolute -right-0.5 -top-0.5 flex h-4 w-4 items-center justify-center rounded-full text-[9px] font-bold"
                    style={{ backgroundColor: theme.colors.primary, color: theme.colors.bg }}
                  >
                    {conv.unread_count > 9 ? '9+' : conv.unread_count}
                  </span>
                )}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline justify-between gap-2">
                  <span
                    className="truncate text-sm"
                    style={{
                      color: theme.colors.text,
                      fontWeight: hasUnread ? 700 : 500,
                    }}
                  >
                    {conv.title}
                  </span>
                  {conv.last_message_at && (
                    <span className="shrink-0 text-[10px]" style={{ color: theme.colors.muted }}>
                      {formatShortTime(conv.last_message_at)}
                    </span>
                  )}
                </div>
                {conv.last_message_snippet && (
                  <p
                    className="mt-0.5 truncate text-xs"
                    style={{
                      color: hasUnread ? theme.colors.text : theme.colors.muted,
                      fontWeight: hasUnread ? 500 : 400,
                    }}
                  >
                    {conv.last_message_snippet}
                  </p>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function formatShortTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / 86_400_000);

  if (diffDays === 0) {
    return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  }
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return date.toLocaleDateString([], { weekday: 'short' });
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
}
